import { PageHeading } from '@src/components/PageHeading/PageHeading';
import './CardClaimPrompt.css';
import { useMatchesMedia, MOBILE_HEADER_MQ } from '@src/components/layout/useMobileHeaderRecede';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Container,
  CreationShell,
  Cluster,
  GameBadge,
  HintError,
  Note,
  ParticipantFace,
  PersonCard,
  showDialog,
  Stack,
  Typography,
} from '@src/components';

export interface CardClaimPromptProps {
  boxName?: string;
  organizerOnly?: boolean;
  name: string;
  picture?: string | null;
  avatar?: string | null;
  /** Runs the claim. Resolving means the browser now owns this card. */
  onClaim: () => Promise<void>;
  error: string | null;
}

/**
 * Stop between a personal link and the card behind it. Claiming ties the card
 * to this browser and locks everyone else out of it, so the person opening the
 * link is asked to check the name first — a forwarded link would otherwise
 * quietly take someone else's place in the game.
 */
export function CardClaimPrompt({
  boxName = '',
  organizerOnly = false,
  name,
  picture,
  avatar,
  onClaim,
  error,
}: CardClaimPromptProps) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const mobile = useMatchesMedia(MOBILE_HEADER_MQ);
  const confirm = async () => {
    setBusy(true);
    try {
      await onClaim();
    } finally {
      setBusy(false);
    }
  };
  const decline = () => {
    if (busy) return;
    showDialog({
      title: t('box.claim.wrongTitle'),
      children: t('box.claim.wrongBody'),
      submit: t('dialog.ok'),
      testId: 'box-claim-wrong',
    });
  };

  if (mobile) {
    return (
      <CreationShell
        mobileFocused
        mobileProgress={false}
        showHeader={false}
        footer={false}
        heading={t(organizerOnly ? 'box.claim.organizerTitle' : 'box.claim.title')}
        title={t(organizerOnly ? 'box.claim.organizerTitle' : 'box.claim.title')}
        step={1}
        steps={1}
        onBack={organizerOnly ? undefined : decline}
        action={
          <Button loading={busy} onClick={confirm} testId="box-claim-yes">
            {t('newBox.continue')}
          </Button>
        }
        error={<HintError error={error} testId="box-claim-error" />}
        testId="box-claim"
      >
        <Cluster gap={4} align="center" wrap={false} testId="box-claim-identity">
          <ParticipantFace avatar={avatar} picture={picture} size="identity" />
          <Typography as="h2" role="title/section">
            {name}
          </Typography>
        </Cluster>
        <Note tone={organizerOnly ? "info" : "warning"} iconAlign="start" testId="box-claim-note">
          <Typography as="p" role="body/strong">
            {t(organizerOnly ? 'box.claim.organizerNoteTitle' : 'box.claim.noteTitle')}
          </Typography>
          <Typography as="p" role="body/sm" color="inherit">
            {t(organizerOnly ? 'box.claim.organizerNoteBody' : 'box.claim.noteBody')}
          </Typography>
        </Note>
      </CreationShell>
    );
  }

  return (
    <Container className="card-claim" testId="box-claim">
      <header className="card-claim__heading">
        <GameBadge name={boxName} testId="box-claim-badge" />
        <PageHeading testId="box-claim-title" title={<>{t(organizerOnly ? 'box.claim.organizerTitle' : 'box.claim.title')}</>} />
      </header>
      <PersonCard
        avatar={<ParticipantFace avatar={avatar} picture={picture} size="identity" />}
        name={name}
        edges={3}
        className="card-claim__card"
        testId="box-claim-identity"
      >
        <Note tone={organizerOnly ? "info" : "warning"} iconAlign="start" testId="box-claim-note">
          <Typography as="p" role="body/strong">
            {t(organizerOnly ? 'box.claim.organizerNoteTitle' : 'box.claim.noteTitle')}
          </Typography>
          <Typography as="p" role="body/sm" color="inherit">
            {t(organizerOnly ? 'box.claim.organizerNoteBody' : 'box.claim.noteBody')}
          </Typography>
        </Note>

        <Stack gap={3} wide>
          <HintError error={error} testId="box-claim-error" />

          <Button
            variant="primary"
            size="large"
            loading={busy}
            onClick={confirm}
            testId="box-claim-yes"
          >
            {t(organizerOnly ? 'newBox.continue' : 'box.claim.yes')}
          </Button>
        </Stack>
        {!organizerOnly && <Button
          variant="ghost"
          className="card-claim__decline"
          disabled={busy}
          onClick={decline}
          testId="box-claim-no"
        >
          {t('box.claim.no')}
        </Button>}
      </PersonCard>
    </Container>
  );
}
